/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { TableCell } from '@mui/material';
import { useNavigate } from 'react-router-dom';

// hooks/redux
import { KeyValue } from '@/main/models/common';
import { insurerApi } from '@/main/api';
import { InsurerFilterRequest } from '@/main/models/request';
import { HeadQuarterBranchFilterEnum } from '@/main/services/enum/HeadQuarterBranchFilterEnum';
import { getCountriesUser, useCountryReducer } from '@/main/store/country';
import {
  getStatusRegister,
  useStatusRegisterReducer
} from '@/main/store/statusRegister';

import { InsurerDataResponse } from '@/main/models/response';
import { PaginationEnum } from '@/main/services/enum/PaginationEnum';
import { RoutesConstants } from '@/main/routes/routesConstants';

// Components
import Button from '@/main/components/shared/button/button';
import Box from '@/main/components/shared/box';
import LoadingComponent from '@/main/components/shared/loading/loadingComponent';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import BoxGrid from '@/main/components/shared/boxGrid/boxGrid';
import StatusComponent from '@/main/components/shared/status/statusComponent';
import KogAutoCompleteControlled from '@/main/components/shared/inputAutoComplete/autoCompleteControlled';
import ResultNotFoundComponent from '@/main/components/shared/resultNotFound/resultNotFoundComponent';
import KogInputTextControlledNew from '@/main/components/shared/inputText/inputTextControlledNew';
import ContainerScreen from '@/main/components/shared/containerScreen/containerLayout';

// Styles
import { KOGTableRow } from '@/main/components/shared/boxGrid/styles';
import { AreaButton, KOGAreaButtons } from '@/main/components/shared/styles';
import { StatusRegisterEnum } from '@/main/services/enum/StatusRegisterEnum';
import {
  getInsurers,
  resetReducerInsurers,
  useInsurerReducer
} from '@/main/store/insurer';
import {
  removeEmptyValues,
  getTranslateByKey,
  getDefaultMessageResultsFound
} from '@/main/services/utils';
import * as S from './styles';
import useFormQueryParams from '@/main/hooks/useformQueryParams';
import { withQueryparams } from '@/main/components/core/queryParams/queryparams';
import { userIsAdmin, userIsInsurer } from '@/main/services/utilsUser';

export interface ISearchInsurerForm {
  country: KeyValue | null;
  company: KeyValue | null;
  rfc: string;
  status: KeyValue | null;
}
interface IGridData {
  data: InsurerDataResponse[];
  totalItems: number;
  isLoading: boolean;
}

interface IPropsInsurerSearchGrid {
  gridData: IGridData;
  formFilters: InsurerFilterRequest | null;
  setFormFilters: (formFilters: InsurerFilterRequest | null) => void;
  getGridData: (formFilters: InsurerFilterRequest) => void;
  hasShowMore: boolean;
  pageIndex: number;
  setPageIndex: (pageIndex: number) => void;
  isLoadingInfiniteScroll: boolean;
}

const INITIAL_GRID_STATE: IGridData = {
  totalItems: 0,
  isLoading: false,
  data: []
};
const InsurerSearchGrid = ({
  gridData,
  formFilters,
  setFormFilters,
  getGridData,
  hasShowMore,
  pageIndex,
  setPageIndex,
  isLoadingInfiniteScroll
}: IPropsInsurerSearchGrid) => {
  const rowHeaders = [
    getTranslateByKey('information.rfc'),
    getTranslateByKey('information.company'),
    getTranslateByKey('information.tradeName'),
    `${getTranslateByKey('information.state')}/${getTranslateByKey(
      'information.city'
    )}`,
    getTranslateByKey('information.status')
  ];
  const titleGrid = getDefaultMessageResultsFound(
    gridData.data.length,
    gridData.totalItems
  );
  const navigate = useNavigate();

  function handleRowBoxGrid(registerGuid: string) {
    navigate(
      `${RoutesConstants.registrationRegisterInsurer}?insurerGuid=${registerGuid}`
    );
  }

  function onLoadSearchGrid() {
    const index = pageIndex + PaginationEnum.PageIndexIntial;
    setPageIndex(index);

    const formFiltersOnLoadSearchGrid = formFilters;
    if (formFiltersOnLoadSearchGrid) {
      formFiltersOnLoadSearchGrid.pageIndex = index;

      setFormFilters(formFiltersOnLoadSearchGrid);
      getGridData(formFiltersOnLoadSearchGrid);
    }
  }

  function renderAddress(insurer: InsurerDataResponse) {
    if (insurer.addresses && insurer.addresses.length > 0) {
      const state = insurer.addresses[0]?.municipality?.city?.state?.name;
      const city = insurer.addresses[0]?.municipality?.city?.name;
      if (state && city) {
        return `${state}/${city}`;
      }
    }
    return <></>;
  }

  if (gridData.isLoading) return <LoadingComponent />;
  if (gridData.data.length === 0 && formFilters !== null)
    return <ResultNotFoundComponent styles={{ marginTop: 10 }} />;
  if (gridData.data.length === 0 && !gridData.isLoading) return null;

  return (
    <BoxGrid
      title={titleGrid}
      rowHeaders={rowHeaders}
      infiniteScrollConfig={{
        dataLength: gridData.data.length,
        fetchData: () => onLoadSearchGrid(),
        hasMore: hasShowMore,
        isLoading: isLoadingInfiniteScroll
      }}>
      {gridData.data.map(insurer => (
        <KOGTableRow
          key={insurer.guid}
          onClick={() => handleRowBoxGrid(insurer.guid)}>
          <TableCell width="20%">{insurer.registrationNumber}</TableCell>
          <TableCell>
            <div style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {insurer.companyName}
            </div>
          </TableCell>
          <TableCell>
            <div style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {insurer.tradeName}
            </div>
          </TableCell>
          <TableCell width="25%">{renderAddress(insurer)}</TableCell>
          <TableCell width="10%">
            <StatusComponent status={insurer?.statusRegister} />
          </TableCell>
        </KOGTableRow>
      ))}
    </BoxGrid>
  );
};

function InsurerScreen() {
  // Hooks & reducers
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const countryReducer = useCountryReducer();
  const statusRegisterReducer = useStatusRegisterReducer();
  const insurerReducer = useInsurerReducer();

  const optionDefaultCountryDrop =
    countryReducer.countriesUser.data !== null &&
      countryReducer.countriesUser.data.length === 1
      ? countryReducer.countriesUser.data[0]
      : null;

  const optionDefaultInsurerDrop =
    insurerReducer.insurers.data !== null &&
      insurerReducer.insurers.data.length === 1
      ? insurerReducer.insurers.data[0]
      : null;

  const INITIAL_FORM_STATE: ISearchInsurerForm = {
    country: optionDefaultCountryDrop as KeyValue | null,
    company: optionDefaultInsurerDrop as KeyValue | null,
    rfc: '',
    status: null as KeyValue | null
  };

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    customHandleSubmit,
    formState: { errors },
    reset
  } = useFormQueryParams<ISearchInsurerForm>({
    mode: 'onChange',
    defaultValues: INITIAL_FORM_STATE,
    mapDefaultValues: {
      country: countryReducer.countriesUser,
      company: insurerReducer.insurers,
      status: statusRegisterReducer.statusRegister,
    },
    onSubmit: onSubmit
  });

  // States
  const [hasShowMore, setHasShowMore] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [gridData, setGridData] = useState<IGridData>(INITIAL_GRID_STATE);
  const [formFilters, setFormFilters] = useState<InsurerFilterRequest | null>(
    null
  );
  const [pageIndex, setPageIndex] = useState(PaginationEnum.PageIndexIntial);
  const [isLoadingInfiniteScroll, setIsLoadingInfiniteScroll] = useState(false);

  const isDisabledInsurerDrop = () =>
    insurerReducer.insurers.data === null || getValues().country === null;

  function callInsurerApiDomain(optionSelected: KeyValue | null, isClearForm: boolean = false) {
    if (!isClearForm) {
      setValue('company', null);
    }

    if (optionSelected !== null) {
      const request: InsurerFilterRequest = {
        guidCountry: String(optionSelected.key),
        type: HeadQuarterBranchFilterEnum.Only_Headquarters,
        allActiveStatus: true
      };
      dispatch(getInsurers(request));
    } else {
      dispatch(resetReducerInsurers());
    }
  }

  function handleFilters() {
    setShowFilters(!showFilters);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function handleNew() {
    navigate(RoutesConstants.registrationRegisterInsurer);
  }

  function handleClear() {
    reset(INITIAL_FORM_STATE);
    if (statusRegisterReducer.statusRegister.data !== null) {
      const keyStatusRegister = String(StatusRegisterEnum.Active);
      const valueStatusRegister = String(
        statusRegisterReducer.statusRegister.data?.find(
          x => x.key === keyStatusRegister
        )?.value
      );
      setValue('status', {
        key: keyStatusRegister,
        value: valueStatusRegister
      });
    }
    setValue('country', optionDefaultCountryDrop);
    setValue('company', optionDefaultInsurerDrop);
    setGridData(INITIAL_GRID_STATE);
    setFormFilters(null);
    setPageIndex(PaginationEnum.PageIndexIntial);
    setHasShowMore(false);
    setIsLoadingInfiniteScroll(false);

    if (optionDefaultCountryDrop != null) {
      callInsurerApiDomain(optionDefaultCountryDrop, true);
    } else {
      dispatch(resetReducerInsurers());
    }
  }

  function onSubmit(formInputs: ISearchInsurerForm) {
    setPageIndex(PaginationEnum.PageIndexIntial);
    setGridData({
      isLoading: true,
      totalItems: 0,
      data: []
    });

    const requestForm: InsurerFilterRequest = {
      guidCountry: String(formInputs.country?.key),
      guidInsurer: formInputs.company?.key
        ? String(formInputs.company.key)
        : null,
      registrationNumber: formInputs.rfc,
      idStatusRegister: formInputs.status?.key
        ? Number(formInputs.status.key)
        : null,
      pageIndex: PaginationEnum.PageIndexIntial,
      pageSize: PaginationEnum.PageSize30
    };

    setFormFilters(requestForm);
    const requestFormatted = removeEmptyValues(requestForm);

    insurerApi.getInsurers(requestFormatted).then(response => {
      setGridData({
        isLoading: false,
        totalItems: response.data.totalNumberOfRecords,
        data: response.data.results
      });
      setHasShowMore(
        response.data.results.length < response.data.totalNumberOfRecords
      );
    });
    setShowFilters(!showFilters);
  }

  function onLoadInfiniteScroll(request: InsurerFilterRequest) {
    setIsLoadingInfiniteScroll(true);
    insurerApi
      .getInsurers(request)
      .then(response => {
        let resultData: InsurerDataResponse[] = [];
        let resultTotalItems: number = 0;
        if (
          response.data &&
          response.data.results &&
          response.data.results.length > 0
        ) {
          resultData = response.data.results;
          resultTotalItems = response.data.totalNumberOfRecords;
        }
        if (gridData.data.length > 0) {
          resultData = [...gridData.data, ...resultData];
        }
        setGridData({
          isLoading: false,
          totalItems: resultTotalItems,
          data: resultData
        });

        // Disable show more
        if (
          resultTotalItems < PaginationEnum.PageSize30 ||
          resultTotalItems === resultData.length
        ) {
          setHasShowMore(false);
        }
      })
      .finally(() => setIsLoadingInfiniteScroll(false));
  }

  useEffect(() => {
    if (countryReducer.countriesUser.data === null) dispatch(getCountriesUser());
    if (statusRegisterReducer.statusRegister.data === null) dispatch(getStatusRegister());

    return () => {
      dispatch(resetReducerInsurers());
    };
  }, []);

  useEffect(() => {
    // Call API's related by country default
    if (optionDefaultCountryDrop) {
      callInsurerApiDomain(optionDefaultCountryDrop);
    }
  }, [optionDefaultCountryDrop]);


  const canCreateNew = userIsAdmin();
  return (
    <ContainerScreen
      hasBackground
      title={`
      ${getTranslateByKey('information.registration')} ${' > '}
        ${getTranslateByKey('information.searchInsurer')}
    `}>
      <S.Container>
        <AreaButton>
          <Button onClick={handleFilters} id="id-searchInsurer-btn-filters">
            {getTranslateByKey('information.filters')}
          </Button>
          {canCreateNew && (
            <Button onClick={handleNew} id="id-searchInsurer-btn-newInsurer">
              {getTranslateByKey('information.newInsurer')}
            </Button>
          )}
        </AreaButton>
        <Box
          showBox={showFilters}
          title={getTranslateByKey('information.filters')}>
          <form onSubmit={customHandleSubmit} noValidate>
            <S.RowInputs>
              <KogAutoCompleteControlled
                label={getTranslateByKey('information.country')}
                nameCustom="country"
                control={control}
                options={countryReducer.countriesUser.data}
                isLoading={countryReducer.countriesUser.isLoading}
                rulesCustom={{ required: true }}
                hasErrorCustom={!!errors.country}
                errorMessageCustom={
                  errors.country?.type === 'required'
                    ? getTranslateByKey('information.countryRequired', true)
                    : ''
                }
                onChangeCustom={(optionSelected: KeyValue | null) => {
                  callInsurerApiDomain(optionSelected);
                }}
                autoHighlightCustom={!!optionDefaultCountryDrop}
                setValueCustom={setValue}
                disabled={optionDefaultCountryDrop !== null}
                id="id-searchInsurer-input-country"
              />
              <KogAutoCompleteControlled
                label={getTranslateByKey('information.company')}
                control={control}
                nameCustom="company"
                rulesCustom={{ required: false }}
                errorMessageCustom=""
                hasErrorCustom={false}
                options={insurerReducer.insurers.data}
                isLoading={insurerReducer.insurers.isLoading}
                onChangeCustom={() => { }}
                disabled={isDisabledInsurerDrop() || !!optionDefaultInsurerDrop}
                autoHighlightCustom={!!optionDefaultInsurerDrop}
                setValueCustom={setValue}
                id="id-searchInsurer-input-company"
              />
              <KogInputTextControlledNew
                label={getTranslateByKey('information.rfc')}
                helperText={
                  errors.rfc?.type === 'minLength'
                    ? getTranslateByKey(
                      'information.rfcMustHaveAtLeast12Digits',
                      true
                    )
                    : ''
                }
                name="rfc"
                error={!!errors.rfc}
                inputProps={{ maxLength: 20 }}
                controllerProps={{ rules: { minLength: 12 } }}
                control={control}
                id="id-searchInsurer-txt-rfc"
              />
            </S.RowInputs>
            <S.RowInputs>
              <KogAutoCompleteControlled
                label={getTranslateByKey('information.status')}
                nameCustom="status"
                valueDefaultKey={String(StatusRegisterEnum.Active)}
                errorMessageCustom=""
                control={control}
                setValueCustom={setValue}
                options={statusRegisterReducer.statusRegister.data}
                isLoading={statusRegisterReducer.statusRegister.isLoading}
                rulesCustom={{ required: false }}
                hasErrorCustom={false}
                onChangeCustom={() => { }}
                id="id-searchInsurer-input-status"
              />
            </S.RowInputs>
            <KOGAreaButtons>
              <SecondaryButton type="submit" id="id-searchInsurer-btn-search">
                {getTranslateByKey('information.search')}
              </SecondaryButton>
              <SecondaryButton onClick={handleClear} id="id-searchInsurer-btn-clear">
                {getTranslateByKey('information.clear')}
              </SecondaryButton>
            </KOGAreaButtons>
          </form>
        </Box>

        <InsurerSearchGrid
          isLoadingInfiniteScroll={isLoadingInfiniteScroll}
          hasShowMore={hasShowMore}
          pageIndex={pageIndex}
          getGridData={(request: InsurerFilterRequest) =>
            onLoadInfiniteScroll(request)
          }
          setPageIndex={setPageIndex}
          gridData={gridData}
          formFilters={formFilters}
          setFormFilters={setFormFilters}
        />
      </S.Container>
    </ContainerScreen>
  );
}

export default withQueryparams(InsurerScreen);
